import { service } from '@ember/service';
import Route from '@ember/routing/route';
import { query } from '@warp-drive/utilities/json-api';
import type { PslfGroup as PslfGroupModel } from 'tio-data/data/resources/pslf-group.type';
import type { PslfProfileExtension as PslfProfileModel } from 'tio-data/data/resources/pslf-profile.ext';
import type { PslfTemplateExtension as PslfTemplateModel } from 'tio-data/data/resources/pslf-template.ext';
import type { Store } from '@warp-drive/core';
import type PartnerService from 'tio-employee/services/partner';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type { PslfField as PslfFieldModel } from 'tio-data/data/resources/pslf-field.type';
import type { IntlService } from 'ember-intl';

export default class AuthenticatedPslfDashboardStartNewRoute extends Route {
  @service declare store: Store;
  @service declare partner: PartnerService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;

  async model() {
    const parentModel = (await this.modelFor('authenticated.pslf')) as {
      pslfProfile: PslfProfileModel;
      groups: PslfGroupModel[];
    };

    const currentEmployee = parentModel.pslfProfile.employee;

    const { content: templateContent } = (await this.store.request(
      query<PslfTemplateModel>(
        'pslf-template',
        {
          include: 'pslf-fields',
          pslfProfileId: parentModel.pslfProfile.id,
        },
        { reload: true, namespace: 'api/v2' }
      )
    )) as { content: { data: PslfTemplateModel[] } };

    // TODO: figure out a better way to fetch the correct template
    const pslfTemplate = templateContent.data?.[0] as PslfTemplateModel | undefined;

    if (!pslfTemplate) {
      console.error(
        '[pslf:start-new] No PSLF template found for profile',
        parentModel.pslfProfile.id
      );
      this.notifications.add(this.intl.t('pslf.start_new.template_unavailable_notification'), {
        appearance: 'error',
      });
      return {
        employee: currentEmployee,
        groups: parentModel.groups,
        pslfTemplate: undefined,
      };
    }

    // HACK: attempting to fix a bug in staging where the template and fields are both loaded but the relationship is not set
    // this is only occurring when navigating from the pslf dashboard, going straight to start-new has no issues
    if (!pslfTemplate.pslfFields?.length) {
      const { content: fieldsContent } = (await this.store.request(
        query<PslfFieldModel>(
          'pslf-field',
          {
            'filter[pslfTemplateId]': pslfTemplate.id,
            pslfProfileId: parentModel.pslfProfile.id,
          },
          { namespace: 'api/v2' }
        )
      )) as { content: { data: PslfFieldModel[] } };

      pslfTemplate.pslfFields = fieldsContent.data;
    }

    return {
      employee: currentEmployee,
      groups: parentModel.groups,
      pslfTemplate,
    };
  }
}
